import React from "react";
import { Box, Checkbox, FormControlLabel } from "@material-ui/core";
import { useNavManager } from "@hooks";
import { CONSULTANT_SLIDER, ConsultantProfileLocation } from "@navigation";
import { KanbanBoardHeader } from "../kanban-board/kanban-board-header";
import { KanbanBoard } from "../kanban-board/kanban-board";
import { ConsultantOpportunityCard } from "./consultnant-opportunity-card/consultant-opportunity-card";
import { FinaliseOpportunityFormModal } from "../components/finalise-opportunity/finalise-opportunity-form-modal";
import { useConsultantOpportunityBoardContext } from "./use-consultant-opportunity-board-context";
import { useLocale } from "@hooks";
import { MarkHighlighter } from "@common";
import { stringNormalize } from "@bonliva-crm/common";
import { ConsultantOpportunityColumnHeader } from "./consultant-opportunity-column-header";
import { ConsultantPipelineNotFound } from "../components/consultant-pipeline-not-found";
import { EditOpportunityFormModal } from "../components/create-opportunity/edit-opportunity-form-modal";

export function ConsultantOpportunityBoard() {
  const { locale } = useLocale();
  const nav = useNavManager();
  const {
    state,
    loading,
    error,
    setBoardUserId,
    handleTextSearch,
    handleShowOld,
    handleResponseThisMonth,
    refetch,
    dispatch,
    onMoveCard,
    onDeleteCard,
    canDeleteCard
  } = useConsultantOpportunityBoardContext();

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (!state && loading) {
    return <div>Loading...</div>;
  }

  if (!state.board) {
    return <ConsultantPipelineNotFound onUserChange={setBoardUserId} />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      flex="1"
      height="90%"
      pl={2}
      pr={2}
      mt={2}
    >
      <FinaliseOpportunityFormModal
        open={!!state.finalizePresentationIssueId}
        issueId={state.finalizePresentationIssueId}
        type={state.finalizePresentationType}
        onCancel={() => {
          dispatch({
            type: "CLOSE_FINALIZE_PRESENTATION_FORM"
          });
          refetch();
        }}
      />

      <EditOpportunityFormModal
        open={!!state.updateOpportunityIssueId}
        issueId={state.updateOpportunityIssueId}
        onClose={() => {
          dispatch({
            type: "CLOSE_UPDATE_OPPORTUNITY_FORM"
          });
          refetch();
        }}
      />

      <Box flex="0 0 0" mb={2}>
        <KanbanBoardHeader
          boardTitle={state.board?.title}
          onUserChange={(userId) => setBoardUserId(userId)}
          onSearch={(v) => handleTextSearch(v)}
          prependFilterPanel={() => (
            <>
              <Box>
                <FormControlLabel
                  value="right"
                  control={<Checkbox color="primary" />}
                  label={locale.showOld}
                  onChange={(_, checked) => handleShowOld(checked)}
                />
              </Box>

              <Box>
                <FormControlLabel
                  value="right"
                  control={<Checkbox color="primary" />}
                  label={locale.showResponseThisMonth}
                  onChange={(_, checked) => handleResponseThisMonth(checked)}
                />
              </Box>
            </>
          )}
        />
      </Box>

      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        width="100%"
        style={{ overflow: "hidden" }}
      >
        <Box flex="1" overflow="auto">
          <KanbanBoard
            state={state.board}
            onMoveCard={onMoveCard}
            renderCard={(card, dragging) => (
              <ConsultantOpportunityCard
                key={card.id}
                card={card}
                dragging={dragging}
                canBeDeleted={canDeleteCard(card.id)}
                onOpen={() => {
                  nav.navManager.open(
                    CONSULTANT_SLIDER,
                    new ConsultantProfileLocation(
                      card.issue.consultantOpportunity.consultant._id
                    )
                  );
                }}
                onEditOpportunity={() => {
                  if (card.issue.consultantOpportunity.originalIssue) {
                    dispatch({
                      type: "EDIT_FINALIZED_PRESENTATION_ACTION",
                      payload: {
                        issueId: card.issue.consultantOpportunity.originalIssue._id
                      }
                    });
                  } else {
                    dispatch({
                      type: "OPEN_UPDATE_OPPORTUNITY_FORM",
                      payload: { issueId: card.issue._id }
                    });
                  }
                }}
                onDeleteOpportunity={() => onDeleteCard(card.id)}
              />
            )}
            renderColumnHeader={(column) => (
              <ConsultantOpportunityColumnHeader column={column} />
            )}
          />
        </Box>
      </Box>

      <MarkHighlighter
        highlightSearch={[state.filters.text, stringNormalize(state.filters.text)]}
        getMarkjsContext={() => document.querySelectorAll(".hl")}
      />
    </Box>
  );
}
